/**
 * The external dependencies
 */

/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/react';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';
import {
  GetChangeDto,
  ListWorkMaterialDto,
  ListWorkPackageDto,
  ListWorkServiceDto,
  ProcessListForChangeDto,
} from '@codefluegel/zeta-change-typescript-client';

/**
 * The internal dependencies
 */
import ProcessItemInfo from './ProcessItemInfo';
import ProcessAccordion from '../ProcessAccordion/ProcessAccordion';
import { getDepartmentById } from '../../utils/utils';
import { useUserHasFullAccess } from '../../utils/permissionHelper';

export type ProcessAccordionItemProps = {
  changeId?: string;
  isImplementedBtnVisible: boolean;
  process: ProcessListForChangeDto;
  workPackages: ListWorkPackageDto[];
  workServices: ListWorkServiceDto[];
  workMaterials: ListWorkMaterialDto[];
  isExpanded: boolean;
  isLoading: boolean;
  handleChange: (fieldName: string, fieldValue: string | boolean) => void;
  handleWorkPackageModal: (workPackageId?: number) => void;
  handleServiceModal: (workServiceId?: number) => void;
  handleMaterialModal: (workMaterialId?: number) => void;
  handleProcessExpand: (processId: number) => void;
  handleUpdateAllWorkPackages: () => void;
  change: GetChangeDto | null;
};

const ProcessAccordionItem = observer(
  ({
    process,
    isImplementedBtnVisible,
    workPackages,
    workMaterials,
    workServices,
    changeId,
    isExpanded,
    isLoading,
    handleChange,
    change,
    handleWorkPackageModal,
    handleServiceModal,
    handleMaterialModal,
    handleProcessExpand,
    handleUpdateAllWorkPackages,
  }: ProcessAccordionItemProps) => {
    const { calculatedHours, timeImpact, releasedHours } = process;

    const department = getDepartmentById(
      process.pId,
      change?.commonProject?.navProcessToCommonProjectRelations,
    );

    const userHasFullAccess = useUserHasFullAccess(change);

    const handleExpand = useCallback(() => {
      handleProcessExpand(process.id);
    }, [process, handleProcessExpand]);

    return (
      <ProcessAccordion
        changeId={changeId}
        workPackages={workPackages}
        workMaterials={workMaterials}
        workServices={workServices}
        expanded={isExpanded}
        process={process}
        department={department}
        status={process.evaluationStatus}
        isImplementedBtnVisible={isImplementedBtnVisible}
        isLoading={isLoading}
        handleChange={handleChange}
        handleWorkPackageModal={handleWorkPackageModal}
        handleServiceModal={handleServiceModal}
        handleMaterialModal={handleMaterialModal}
        handleExpand={handleExpand}
        handleUpdateAllWorkPackages={handleUpdateAllWorkPackages}
        SummaryLabels={
          <ProcessItemInfo
            workPackages={workPackages}
            workServices={workServices}
            workMaterials={workMaterials}
            calculatedHours={calculatedHours}
            releasedHours={releasedHours}
            timeImpact={timeImpact}
            process={process}
            showCalculation={false}
            showImplementation
            userHasFullAccess={userHasFullAccess}
          />
        }
      />
    );
  },
);

export default ProcessAccordionItem;
